import { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import type { FC } from 'react';
import {
  makeStyles,
  tokens,
  mergeClasses,
  Text,
  Link,
  Button,
  Tooltip,
} from '@coreai/fluentui-react';
import {
  OptionsRegular,
  OptionsFilled,
  DatabaseMultipleRegular,
  DatabaseMultipleFilled,
  CodeRegular,
  CodeFilled,
  SettingsRegular,
  SettingsFilled,
  BeakerRegular,
  BeakerFilled,
  InfoRegular,
  InfoFilled,
  PlugConnectedRegular,
  PlugConnectedFilled,
  SlideTextRegular,
  SlideTextFilled,
  BotSparkleRegular,
  BotSparkleFilled,
  ArrowMaximizeRegular,
} from '@fluentui/react-icons';
import type { FluentIcon } from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { useNodeEditorContext, type EditorTab } from './useNodeEditorContext';
import { NodeEditorTabBody } from './NodeEditorTabBody';
import { ConnectionErrorBanner } from '../compact/ConnectionErrorBanner';
import { useAdvancedParamCount } from './useAdvancedParamCount';
import { ADVANCED_PARAMS_TAB } from './editorTabs';
import { NodeCodeDiscardConfirmation } from '../NodeCodeDiscardDialog';
import { telemetryService } from '@/services/telemetryService';

/**
 * Drawer information architecture — the redesigned node editor experience.
 *
 * This is the vertical-side-nav layout used by default when the editor is in
 * configuration (non-execution) mode (the legacy modal is opt-in via the
 * `nodeEditorLegacyModal` flag).
 * It is a pure UX reshaping: every section renders the exact same
 * {@link NodeEditorTabBody} content the legacy three-panel `ExpandedView`
 * renders, driven by the exact same {@link useNodeEditorContext} derivations.
 * No node-editing logic is duplicated or changed here.
 *
 * The legacy horizontal tabs collapse into a small set of scannable sections:
 * Configure (parameters + tool/harness), Connect (connection selection),
 * Knowledge, Code
 * (code), Settings, Test data (mock), and Details (the About tab). For Foundry
 * agents the rich `about` tab folds into Configure instead (see
 * {@link resolveSectionForTab}). Execution (monitoring) mode is intentionally NOT
 * handled here — the caller renders the proven `ExpandedView` run-data layout
 * for that path.
 */

type DrawerSectionId =
  | 'configure'
  | 'harness'
  | 'connect'
  | 'knowledge'
  | 'advanced'
  | 'inspect'
  | 'settings'
  | 'testdata'
  | 'details';

interface DrawerSectionDef {
  id: DrawerSectionId;
  label: string;
  icon: FluentIcon;
  iconActive: FluentIcon;
}

/**
 * Section order — Connect leads for connector nodes.
 *
 * `Connect` is FIRST in the array. The rail auto-filters out sections that
 * don't apply to the current node, so:
 *
 *  • Connector-required nodes (Http, service actions, agents) render as:
 *      Connect → Parameters → Harness → Knowledge → Advanced → Code →
 *      Run settings → Test data → About
 *    Authors see the section they must fill first before Parameters becomes
 *    editable. Once the first connection is picked, the drawer auto-returns
 *    to Parameters (see the "first-connect" effect below) so the connect step
 *    doesn't feel like a permanent detour.
 *
 *  • Non-connector nodes (Compose, Condition, most triggers) don't include
 *    Connect in `availableTabs`, so Parameters naturally becomes the first
 *    visible section — no dynamic reorder needed.
 *
 * The rest of the ordering:
 *  - Harness (agent-only) sits right after Parameters — closely tied to
 *    authoring; skipped for non-agents.
 *  - Knowledge → Advanced sits with the authoring cluster.
 *  - Code → Run settings → Test data → About sit at the end (less
 *    frequently touched; end-of-list matches Fluent drawer patterns).
 */
const SECTION_DEFS: DrawerSectionDef[] = [
  {
    id: 'connect',
    label: 'Connect',
    icon: PlugConnectedRegular,
    iconActive: PlugConnectedFilled,
  },
  {
    id: 'configure',
    label: 'Parameters',
    icon: OptionsRegular,
    iconActive: OptionsFilled,
  },
  {
    id: 'harness',
    label: 'Harness',
    icon: BotSparkleRegular,
    iconActive: BotSparkleFilled,
  },
  {
    id: 'knowledge',
    label: 'Knowledge',
    icon: DatabaseMultipleRegular,
    iconActive: DatabaseMultipleFilled,
  },
  {
    id: 'advanced',
    label: 'Advanced',
    icon: SlideTextRegular,
    iconActive: SlideTextFilled,
  },
  {
    id: 'inspect',
    label: 'Code',
    icon: CodeRegular,
    iconActive: CodeFilled,
  },
  {
    id: 'settings',
    label: 'Run settings',
    icon: SettingsRegular,
    iconActive: SettingsFilled,
  },
  {
    id: 'testdata',
    label: 'Test data',
    icon: BeakerRegular,
    iconActive: BeakerFilled,
  },
  {
    id: 'details',
    label: 'About',
    icon: InfoRegular,
    iconActive: InfoFilled,
  },
];

/**
 * Maps every legacy editor tab to the drawer section that hosts it. Inner tool
 * config and MCP config fold into Configure (they are parts of configuring the
 * node). Connection selection lives in its own Connect section so a long
 * connection list never pushes the parameter fields down, and so the entry
 * point mirrors the peer-tab pattern of the legacy `ExpandedView`.
 *
 * `agent-harness` is a first-class peer section (Runtime) for Coding, Foundry,
 * and Hosted variants — same rank as Configure. This mirrors the legacy Agent
 * Harness tab authors used to reach directly from the tab strip, and matches
 * the plan's Node Details IA (Configure = "what the agent does", Runtime =
 * "what platform runs the agent").
 *
 * `about` hosts the generic About view (a passive info tab) for every variant.
 * Foundry agents used to hijack it for their capabilities panel; that panel
 * now lives in the Harness section — see the Phase 2 refactor.
 */
const SECTION_FOR_TAB: Record<EditorTab, DrawerSectionId> = {
  parameters: 'configure',
  connection: 'connect',
  'agent-harness': 'harness',
  tool: 'configure',
  'mcp-config': 'configure',
  knowledge: 'knowledge',
  [ADVANCED_PARAMS_TAB]: 'advanced',
  code: 'inspect',
  settings: 'settings',
  mock: 'testdata',
  about: 'details',
};

/**
 * Render order inside Configure: tool identity/configuration before operation
 * parameters, so essential Tool Name precedes optional operation fields.
 * MCP config is independent (MCP tools have no operation parameters). `agent-harness` is
 * intentionally NOT here — it lives in its own peer section (Harness) now.
 * `about` is also NOT here — SECTION_FOR_TAB routes it to `details`, so it
 * can never appear inside Configure. Only tabs present in `availableTabs`
 * are rendered. When a required connection is missing the section still
 * renders normally (mirroring the legacy modal): the `ConnectionErrorBanner`
 * at the top prompts the user to configure a connection and navigates to
 * the Connect section on click.
 */
const CONFIGURE_TAB_ORDER: EditorTab[] = [
  'tool',
  'parameters',
  'mcp-config',
];

const useStyles = makeStyles({
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    minHeight: 0,
    overflow: 'hidden',
  },
  sideNav: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    width: '88px',
    paddingTop: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    rowGap: tokens.spacingVerticalXXS,
    borderRight: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  navItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: tokens.spacingVerticalXXS,
    width: '100%',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    border: 'none',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyBase,
    textAlign: 'center',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
      color: tokens.colorNeutralForeground1,
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground2Pressed,
    },
  },
  navItemActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Selected,
      color: tokens.colorNeutralForeground1,
    },
  },
  navIcon: {
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
  },
  navLabel: {
    lineHeight: tokens.lineHeightBase200,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sectionContent: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    rowGap: tokens.spacingVerticalM,
    ...customScrollbarStyles,
  },
  sectionContentFill: {
    overflow: 'hidden',
  },
  sectionTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexShrink: 0,
  },
  // Footer note in the Configure section that points users to the peer
  // Advanced section. Understated by design — a Linear/Vercel-style hint,
  // not a marketing banner. Sits inside the scrolling section so long
  // Configure content pushes it below the fold naturally.
  advancedFooterNote: {
    marginTop: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  // Wrapper for each stacked tab body inside a section. Tab bodies (e.g.
  // ConnectionTab) use `flex: 1; min-height: 0` so they fill a *dedicated*
  // modal tab panel, but the drawer stacks several bodies inside one scrolling
  // section — there `flex: 1` lets them collapse to zero height. A block-level
  // wrapper neutralizes the child's flex sizing (flex item props don't apply in
  // a block container) so each body sizes to its content, and `flexShrink: 0`
  // keeps the wrapper from being compressed by its flex-column parent; the
  // section (`sectionContent`) owns the scroll.
  stackedBody: {
    display: 'block',
    flexShrink: 0,
  },
  fillBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
});

export const NodeEditorDrawerContent: FC = () => {
  const styles = useStyles();
  const ctx = useNodeEditorContext();
  const initialTab = useNodeEditorStore((state) => state.initialTab);
  const clearInitialTab = useNodeEditorStore((state) => state.clearInitialTab);
  const isCodeDraftDirty = useNodeEditorStore(
    (state) => state.nodeCodeDraft?.isDirty ?? false,
  );
  const discardNodeCodeDraft = useNodeEditorStore(
    (state) => state.discardNodeCodeDraft,
  );
  const setNodeCodeExpanded = useNodeEditorStore(
    (state) => state.setNodeCodeExpanded,
  );

  const navRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Section-for-tab lookup is a static map now that the Foundry `about`
  // override has been retired (Foundry capabilities live in `agent-harness`).
  const sectionForTab = useCallback(
    (tabId: EditorTab): DrawerSectionId => SECTION_FOR_TAB[tabId],
    [],
  );

  // Advanced-parameter count is derived at the shell level from the shared
  // {@link useAdvancedParamCount} hook — NOT reported up from ParameterList's
  // mount. Pushing this data upward via a callback would break in two ways:
  // (a) the count would clobber itself across nodes with the same advanced
  // count (effect deps don't re-fire when the number stays constant, so a
  // parent reset wins the race), and (b) navigating away from Configure
  // would leave the reporter unmounted, so the section could not react to
  // manifest changes made while the user was on a peer section. Deriving
  // straight from the store keeps `hasAdvancedParams` correct regardless of
  // which section is active or when the user opened the drawer.
  const advancedCount = useAdvancedParamCount(ctx.node);

  // Latch advanced-param presence per node (issue #3446).
  //
  // `advancedCount` is derived from store `parameterDefinitions`, which is
  // transiently reset to `null` by `openEditor`, `refreshFromWorkflow`,
  // `applyNodeCodeDraft`, `reloadNodeCodeDraft` and `resetEditor`, then
  // re-populated asynchronously by ParameterList. For managed/swagger
  // connectors the advanced params exist *only* in `parameterDefinitions`, so
  // during those windows (a live draft sync on Canary, the first-open manifest
  // race, etc.) the count momentarily reads 0. Without latching, that transient
  // 0 drops the Advanced section out of `sections`, and the unavailable-section
  // guard below would yank the user off Advanced — the reported symptom.
  //
  // The latch is monotonic for the life of a node: once advanced params are
  // observed it stays true until the canvas node identity changes. On the first
  // render after a node switch (before the latch effect runs) we fall back to
  // the raw count so a stale latch never leaks across nodes.
  //
  // Tradeoff: a `refreshFromWorkflow` code edit can swap the operation while
  // keeping the same node id, so a node that loses its advanced params without
  // a node switch keeps the Advanced section. That is acceptable — the section
  // body renders its truthful "no advanced parameters" empty state, never the
  // Connection content of #3446.
  const nodeId = ctx.node?.id;
  const [advancedLatch, setAdvancedLatch] = useState<{
    id: string | undefined;
    value: boolean;
  }>(() => ({ id: nodeId, value: advancedCount > 0 }));
  useEffect(() => {
    setAdvancedLatch((prev) => {
      if (prev.id !== nodeId) return { id: nodeId, value: advancedCount > 0 };
      if (advancedCount > 0 && !prev.value) return { id: nodeId, value: true };
      return prev;
    });
  }, [nodeId, advancedCount]);
  const hasAdvancedParams =
    advancedLatch.id === nodeId ? advancedLatch.value : advancedCount > 0;

  // The sections that actually have content for this node, in canonical order.
  // Advanced is filtered out when the node has no advanced parameters (no
  // dead nav item).
  const sections = useMemo(
    () =>
      SECTION_DEFS.filter((def) => {
        if (def.id === 'advanced') {
          return hasAdvancedParams;
        }
        return ctx.availableTabs.some(
          (tab) => sectionForTab(tab.id) === def.id,
        );
      }),
    [ctx.availableTabs, sectionForTab, hasAdvancedParams],
  );

  // Initial section resolution:
  //   1. A caller-requested `initialTab` (e.g. "open to Settings", or clicking
  //      an unconfigured-tool badge) wins — matches ExpandedView's initialTab
  //      handling.
  //   2. Otherwise, if this operation requires a connection and none has been
  //      selected yet, land on Connect so the user's first action is picking a
  //      connection (a UX improvement over the legacy modal, which always
  //      defaulted to Parameters and relied on the ConnectionErrorBanner to
  //      redirect).
  //   3. Otherwise Configure — the highest-frequency destination.
  const [activeSection, setActiveSection] = useState<DrawerSectionId>(() => {
    const seeded = initialTab ? SECTION_FOR_TAB[initialTab] : undefined;
    if (seeded) return seeded;
    if (ctx.showConnectionTab && !ctx.connectionName) return 'connect';
    return 'configure';
  });
  const [pendingSection, setPendingSection] =
    useState<DrawerSectionId | null>(null);

  // Switch sections and move keyboard focus to the destination nav tab. Used
  // for programmatic transitions (the Configure→Connect banner and the
  // Connect→Configure auto-return) where the element that triggered the switch
  // unmounts — without this, focus would fall to <body> (WCAG 2.4.3/2.4.7).
  const focusPendingSection = useRef(false);
  const goToSection = useCallback((id: DrawerSectionId) => {
    focusPendingSection.current = true;
    setActiveSection(id);
  }, []);

  const requestUserSectionChange = useCallback(
    (id: DrawerSectionId): boolean => {
      if (id === activeSection) return true;
      if (activeSection === 'inspect' && isCodeDraftDirty) {
        setPendingSection(id);
        return false;
      }
      setActiveSection(id);
      return true;
    },
    [activeSection, isCodeDraftDirty],
  );

  const confirmSectionChange = useCallback(() => {
    if (!pendingSection) return;
    discardNodeCodeDraft();
    const nextSection = pendingSection;
    setPendingSection(null);
    goToSection(nextSection);
  }, [discardNodeCodeDraft, goToSection, pendingSection]);

  const expandCodeEditor = useCallback(() => {
    setNodeCodeExpanded(true);
    telemetryService.trackEvent('workflow.node_code.expanded');
  }, [setNodeCodeExpanded]);

  useLayoutEffect(() => {
    if (!focusPendingSection.current) return;
    focusPendingSection.current = false;
    const index = sections.findIndex((section) => section.id === activeSection);
    if (index >= 0) navRefs.current[index]?.focus();
  }, [activeSection, sections]);

  // Honor a caller-requested initial tab (e.g. "open to Settings", or clicking
  // an unconfigured-tool badge). Mirrors ExpandedView's initialTab handling.
  useEffect(() => {
    if (initialTab) {
      const seeded = sectionForTab(initialTab);
      if (seeded) {
        setActiveSection(seeded);
      }
      clearInitialTab();
    }
  }, [initialTab, clearInitialTab, sectionForTab]);

  // The fallback destination when the active section becomes unavailable
  // (node variant/operation change): Configure — the node's primary content —
  // never `sections[0]`, which is Connect for connector nodes. Falling back to
  // Connect is what surfaced Connection content in place of Advanced (#3446).
  // Shared with the render-time `activeDef` below so no single frame can
  // resolve to Connect.
  const fallbackSection =
    sections.find((section) => section.id === 'configure') ?? sections[0];

  // If the active section becomes unavailable, correct it *before paint* with a
  // layout effect. The invalid render commits with `activeSection` still set to
  // the vanished section, so a passive effect would let one frame expose the
  // fallback content with no selected rail item and focus on <body>. Routing
  // through `goToSection` also moves keyboard focus to the destination, whose
  // predecessor rail button has unmounted (WCAG 2.4.3/2.4.7).
  useLayoutEffect(() => {
    if (sections.some((section) => section.id === activeSection)) return;
    if (fallbackSection) {
      goToSection(fallbackSection.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- goToSection is stable
  }, [sections, activeSection, fallbackSection]);

  // Auto-return to Configure after the *first* connection is established while
  // on the Connect section (the auto-landing flow above). Changing an existing
  // connection keeps the user on Connect (they navigated there deliberately);
  // the always-visible rail lets them return whenever they choose.
  const prevConnectionName = useRef<string | undefined>(ctx.connectionName);

  // The drawer content is reused (not remounted) when the user selects a
  // different canvas node, so reset the first-connect tracking on a node change.
  // Otherwise a connection name carried over from the previous node could read
  // as a "just connected" transition and spuriously yank the new node's editor
  // to Configure. Defined before the auto-return effect so it runs first in the
  // same commit.
  useEffect(() => {
    prevConnectionName.current = ctx.connectionName;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on node identity only
  }, [ctx.node?.id]);

  useEffect(() => {
    const justConnected = !prevConnectionName.current && !!ctx.connectionName;
    if (activeSection === 'connect' && justConnected) {
      goToSection('configure');
    }
    prevConnectionName.current = ctx.connectionName;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- goToSection is stable
  }, [activeSection, ctx.connectionName]);

  const tabsForSection = useCallback(
    (sectionId: DrawerSectionId): EditorTab[] => {
      // The Advanced section renders a synthetic tab that isn't in
      // `availableTabs` (it isn't a real legacy tab — it's a drawer-only
      // render mode of ParameterList). Inject it whenever the parameters tab
      // is available and the node has advanced params.
      if (sectionId === 'advanced') {
        const hasParametersTab = ctx.availableTabs.some(
          (tab) => tab.id === 'parameters',
        );
        return hasParametersTab && hasAdvancedParams
          ? [ADVANCED_PARAMS_TAB]
          : [];
      }
      const ids = ctx.availableTabs
        .map((tab) => tab.id)
        .filter((id) => sectionForTab(id) === sectionId);
      if (sectionId === 'configure') {
        return CONFIGURE_TAB_ORDER.filter((tab) => ids.includes(tab));
      }
      return ids;
    },
    [ctx.availableTabs, sectionForTab, hasAdvancedParams],
  );

  // Roving-tabindex arrow navigation across the vertical nav.
  const handleNavKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const currentIndex = sections.findIndex(
        (section) => section.id === activeSection,
      );
      if (currentIndex === -1) return;
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex =
        (currentIndex + delta + sections.length) % sections.length;
      const next = sections[nextIndex];
      if (!next) return;
      if (requestUserSectionChange(next.id)) {
        navRefs.current[nextIndex]?.focus();
      }
    },
    [sections, activeSection, requestUserSectionChange],
  );

  if (!ctx.node) return null;

  const activeDef =
    sections.find((section) => section.id === activeSection) ?? fallbackSection;
  const activeTabs = activeDef ? tabsForSection(activeDef.id) : [];

  return (
    <div className={styles.body}>
      <div
        className={styles.sideNav}
        role="tablist"
        aria-orientation="vertical"
        aria-label="Node configuration sections"
        onKeyDown={handleNavKeyDown}
      >
        {sections.map((section, index) => {
          const isActive = section.id === activeSection;
          const Icon = isActive ? section.iconActive : section.icon;
          return (
            <button
              key={section.id}
              ref={(el) => {
                navRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              id={`node-editor-drawer-tab-${section.id}`}
              aria-controls="node-editor-drawer-tabpanel"
              className={mergeClasses(
                styles.navItem,
                isActive && styles.navItemActive,
              )}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => requestUserSectionChange(section.id)}
            >
              <Icon className={styles.navIcon} aria-hidden />
              <span className={styles.navLabel}>{section.label}</span>
            </button>
          );
        })}
      </div>

      <div
        className={mergeClasses(
          styles.sectionContent,
          activeSection === 'inspect' && styles.sectionContentFill,
        )}
        role="tabpanel"
        id="node-editor-drawer-tabpanel"
        aria-labelledby={
          activeDef ? `node-editor-drawer-tab-${activeDef.id}` : undefined
        }
        tabIndex={0}
      >
        {activeDef && (
          <div className={styles.sectionHeader}>
            <Text as="h2" className={styles.sectionTitle}>
              {activeDef.label}
            </Text>
            {activeSection === 'inspect' && (
              <Tooltip content="Open larger editor" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<ArrowMaximizeRegular />}
                  onClick={expandCodeEditor}
                  aria-label="Expand node code editor"
                />
              </Tooltip>
            )}
          </div>
        )}
        {activeSection === 'configure' && ctx.showConnectionTab && (
          <ConnectionErrorBanner
            nodeId={ctx.node.id}
            {...(ctx.nodeType !== undefined
              ? { operationType: ctx.nodeType }
              : {})}
            onNavigateToConnection={() => goToSection('connect')}
          />
        )}
        {activeTabs.map((tab) => (
          <div
            key={tab}
            className={mergeClasses(
              styles.stackedBody,
              activeSection === 'inspect' && styles.fillBody,
            )}
          >
            <NodeEditorTabBody
              tab={tab}
              ctx={ctx}
              onNavigateToHarness={() => goToSection('harness')}
              {...(tab === 'parameters' && activeSection === 'configure'
                ? { parameterListMode: 'main' as const }
                : {})}
            />
          </div>
        ))}
        {/* Footer hint pointing users at the Advanced peer section. Only
            renders when the node actually has advanced params — no dead
            links. Content-writer voice: understated, no exclamation, no
            "click here" pattern. Fluent `Link as="button"` gives the correct
            same-panel navigation semantics (link role) without fighting a
            Button's default padding/min-height. */}
        {activeSection === 'configure' && hasAdvancedParams && (
          <div className={styles.advancedFooterNote}>
            <Text size={200}>More parameters in</Text>
            <Link as="button" onClick={() => goToSection('advanced')}>
              Advanced
            </Link>
          </div>
        )}
      </div>
      <NodeCodeDiscardConfirmation
        open={pendingSection !== null}
        destinationDescription={`open ${
          sections.find((section) => section.id === pendingSection)?.label ??
          'that section'
        }`}
        onCancel={() => setPendingSection(null)}
        onConfirm={confirmSectionChange}
      />
    </div>
  );
};

NodeEditorDrawerContent.displayName = 'NodeEditorDrawerContent';
